import { useState, useRef, useEffect } from 'react'
import { ChevronDown, X, Folder, CheckSquare, Square, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useUserFolders } from "../../hooks/useUserFolders"
import { useScreenSize } from '@/hooks/use-screen-size'
import { useChatContext } from '../../hooks/useChatContext'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'

const getMaxFoldersToShow = (screenSize: 'sm' | 'md' | 'lg' | 'xl') => {
  switch (screenSize) {
    case 'sm': return 1
    case 'md': return 2
    case 'lg': return 3
    case 'xl': return 4
    default: return 3
  }
}

export default function FolderSelector() {
  const { t } = useTranslation();
  const { setFoldersId, foldersId } = useChatContext();
  const { folders, isLoadingFolders } = useUserFolders()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const screenSize = useScreenSize()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, []);

  // Focus on input when dropdown opens 
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // clears search after dropdown closes 
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
    }
  }, [isOpen])

  const activeFolders = folders.filter(folder => folder.is_active && !folder.deleted_at);

  const filteredFolders = activeFolders.filter(folder =>
    folder.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFolderToggle = (folderId: string) => {
    setFoldersId((prev) => 
      prev.includes(folderId) 
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    )
  }

  const handleRemoveFolder = (folderId: string) => {
    setFoldersId(prev => prev.filter(id => id !== folderId))
  }

  const handleSelectAll = () => {
    const filteredIds = filteredFolders.map(folder => folder.external_id)
    setFoldersId(prev => {
      const newIds = [...prev]
      filteredIds.forEach(id => {
        if (!newIds.includes(id)) {
          newIds.push(id)
        }
      })
      return newIds
    })
  }

  const handleDeselectAll = () => {
    const filteredIds = filteredFolders.map(folder => folder.external_id)
    setFoldersId(prev => prev.filter(id => !filteredIds.includes(id)))
  }

  const getSelectedFolderNames = () => {
    return foldersId.map(id => {
      const folder = activeFolders.find(f => f.external_id === id)
      return folder ? folder.name : ''
    }).filter(Boolean)
  }

  const maxFoldersToShow = getMaxFoldersToShow(screenSize)
  const selectedFolderNames = getSelectedFolderNames()
  const isDesktop = screenSize === 'lg' || screenSize === 'xl'
  const isMobile = screenSize === 'sm'

  const allFilteredSelected = filteredFolders.length > 0 &&  filteredFolders.every(folder => foldersId.includes(folder.external_id))
  const someFilteredSelected = filteredFolders.some(folder => foldersId.includes(folder.external_id))

  if (isLoadingFolders) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 border-b">
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
          <div className="w-20 h-4 bg-gray-300 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="border-b bg-background">
      <div className="flex items-center gap-2 px-4 py-2">
        <div className="relative" ref={dropdownRef}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <Folder className="w-3 h-3 mr-1" />
            {t(tokens.workstation.folders.label)}
            <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </Button>

          {isOpen && (
            <Card className="absolute bottom-full left-0 mb-1 w-72 max-h-80 z-[9999] shadow-lg border bg-background">
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={t(tokens.workstation.folders.searchFolders) || "Buscar pastas..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-7 pr-3 py-1.5 text-xs border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              {filteredFolders.length > 0 && (
                <div className="px-3 py-2 border-b bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAll}
                      disabled={allFilteredSelected}
                      className="h-6 px-2 text-xs"
                    >
                      <CheckSquare className="w-3 h-3 mr-1" />
                      {t(tokens.workstation.folders.selectAllFolders) || "Marcar todos"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleDeselectAll}
                      disabled={!someFilteredSelected}
                      className="h-6 px-2 text-xs"
                    >
                      <Square className="w-3 h-3 mr-1" />
                      {t(tokens.workstation.folders.deselectAllFolders) || "Desmarcar todos"}
                    </Button>
                  </div>
                </div>
              )}

              <div className="max-h-48 overflow-y-auto">
                {filteredFolders.length === 0 ? (
                  <div className="text-xs text-muted-foreground p-3">
                    {searchTerm ? 
                      (t(tokens.workstation.folders.noResults) || "Nenhuma pasta encontrada") :
                      t(tokens.workstation.folders.empty)
                    }
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {filteredFolders.map((folder) => (
                      <label
                        key={folder.external_id}
                        className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={foldersId.includes(folder.external_id)}
                          onChange={() => handleFolderToggle(folder.external_id)}
                          className="w-3 h-3 rounded border border-input"
                        />
                        <span className="text-xs flex-1 truncate" title={folder.name}>
                          {folder.name}
                        </span>
                        {(folder.total_files ?? 0) >= 0 && (
                          <span className="text-xs text-muted-foreground">
                            ({folder.total_files})
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        <div className="flex items-center gap-1 flex-1 min-w-0">
          {foldersId.length === 0 ? (
            <span className="text-xs text-muted-foreground">
              {t(tokens.workstation.folders.all_knowledge)}
            </span>
          ) : (
            <>
              {isDesktop && (
                <>
                  {selectedFolderNames.slice(0, maxFoldersToShow).map((folderName, index) => {
                    const folderId = foldersId[index]
                    return (
                      <div
                        key={folderId}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs shrink-0"
                      >
                        <span className="max-w-16 sm:max-w-20 truncate" title={folderName}>
                          {folderName}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFolder(folderId)}
                          className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    )
                  })}
                  {foldersId.length > maxFoldersToShow && (
                    <span 
                      className="text-xs text-muted-foreground cursor-help"
                      title={selectedFolderNames.slice(maxFoldersToShow).join(', ')}
                    >
                      +{foldersId.length - maxFoldersToShow}
                    </span>
                  )}
                </>
              )}

              {!isDesktop && !isMobile && (
                <>
                  {selectedFolderNames.slice(0, maxFoldersToShow).map((folderName, index) => {
                    const folderId = foldersId[index]
                    return (
                      <div
                        key={folderId}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs shrink-0"
                      >
                        <span className="max-w-16 sm:max-w-20 truncate" title={folderName}>
                          {folderName}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFolder(folderId)}
                          className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    )
                  })}
                  {foldersId.length > maxFoldersToShow && (
                    <span className="text-xs text-muted-foreground">
                      ...
                    </span>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {foldersId.length > 0 && !isDesktop && (
          <span className="text-xs text-muted-foreground shrink-0">
            {foldersId.length} selecionada{foldersId.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  )
}